import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import i18next from 'i18next'
import { useTranslation, Trans } from 'react-i18next'
import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline'
import { faLayerGroup } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import BackButton from '../BackButton'
import ProgressRail from '../kits/ProgressRail'
import WyborModelu from './WyborModelu'
import WynikiDoboru from './WynikiDoboru'
import IkonaAsystenta from '../ikony/IkonaAsystenta'
import { GRUPY_NARZEDZI } from '../../config/nawigacja'
import { czyAktywna } from '../nawigacja/ListaNarzedzi'

/**
 * Wspólny szkielet kreatorów doboru: falownika, modułu i magazynu.
 *
 * Trzy osobne kopie rozjechałyby się przy pierwszej zmianie — a zmian tu było
 * już kilka (pasek postępu, kolejność pytań, zachowanie przycisku przy
 * niepełnych odpowiedziach) i każda dotyczyła wszystkich trzech tak samo.
 *
 * ═══ FORMA: STRONA, NIE OKNO ═══
 * Pytania jedno pod drugim, wszystkie widoczne, z możliwością cofnięcia się
 * wzrokiem do wcześniejszej odpowiedzi. Modal wymuszał liniowość i nie dało się
 * sprawdzić poprzedniej odpowiedzi bez klikania wstecz.
 *
 * ═══ CZTERY RODZAJE PYTAŃ ═══
 * `wybor`    — kafelki z opcjami (domyślny),
 * `liczba`   — pole liczbowe z jednostką, dla mocy, sztuk i zużycia,
 * `lista`    — lista rozwijana, gdy opcji jest kilkanaście (marki falowników),
 * `szukanie` — pole z podpowiedziami z katalogu, gdy odpowiedzią jest KONKRETNY
 *              model, a lista rozwijana miałaby setki pozycji.
 */

/** Podpis w pasku postępu dla pytania z przełącznikiem jednostek. */
const opisJednostki = (p, v) => {
  if (!v?.ile) return i18next.t('asystent:szkielet.pominiete')
  const j = p.jednostki.find((x) => x.v === v.jednostka) ?? p.jednostki[0]
  return `${String(v.ile).replace('.', ',')} ${j.jednostka}`
}

/** Podpis dla pytania o model: nazwa, sam prąd albo pominięcie. */
// Wielokrotny wybór (cele w trybie prostym, P4 2026-10-25): odpowiedź to lista wartości, pusta lista też jest odpowiedzią.
// `skrot`: na osi po prawej dwie pierwsze i „…”, bo pełna lista rozpychała oś na całą szerokość (przegląd 2026-10-24).
const opisWielu = (p, v, { skrot = false } = {}) => {
  const etykiety = (Array.isArray(v) ? v : []).map((x) => (p.opcje || []).find((o) => o.v === x)?.l).filter(Boolean)
  if (!etykiety.length) return i18next.t('asystent:szkielet.pominiete')
  return skrot && etykiety.length > 2 ? `${etykiety.slice(0, 2).join(', ')}, …` : etykiety.join(', ')
}

const opisModelu = (v) => {
  if (v?.model?.etykieta) return v.model.etykieta
  if (v?.impp) return `${String(v.impp).replace('.', ',')} A`
  return i18next.t('asystent:szkielet.nie_znam')
}

/**
 * Odpowiedź słowami (podsumowanie kryteriów, podsumowanie w kreatorze krokowym): wybór, lista, liczba z jednostką,
 * model, własny komponent albo „pominięte”.
 */
export const opisOdpowiedzi = (p, v) => {
  const pominiete = i18next.t('asystent:szkielet.pominiete')
  if (v === null && !p.jednostki) return pominiete
  if (p.typ === 'komponent') return p.opisOdpowiedzi?.(v) || pominiete
  if (p.jednostki) return opisJednostki(p, v)
  if (p.typ === 'liczba') return `${String(v).replace('.', ',')} ${p.liczba.jednostka}`
  if (p.typ === 'szukanie') return opisModelu(v)
  if (p.typ === 'wielokrotny') return opisWielu(p, v)
  const opcja = (p.opcje || []).find((o) => o.v === v)
  return opcja ? opcja.l : String(v)
}

const Etap = ({ numer, pytanie, wartosc, odpowiedzi, onOdpowiedz, onPodglad, onWstecz, aktywny, refEtapu }) => {
  const { t } = useTranslation('asystent')
  const odpowiedziano = wartosc !== undefined && wartosc !== ''
  const zJednostka = Boolean(pytanie.jednostki)
  const [robocza, setRobocza] = useState((zJednostka ? wartosc?.ile : wartosc) ?? '')
  const wielokrotny = pytanie.typ === 'wielokrotny'
  // Zaznaczenia przed „Dalej”: pierwsze kliknięcie nie zamyka kroku, bo zwykle zaznacza się kilka celów naraz.
  const [zaznaczone, setZaznaczone] = useState(Array.isArray(wartosc) ? wartosc : [])
  useEffect(() => { if (Array.isArray(wartosc)) setZaznaczone(wartosc) }, [wartosc])
  const przelacz = (v) => {
    const nowe = zaznaczone.includes(v) ? zaznaczone.filter((x) => x !== v) : [...zaznaczone, v]
    setZaznaczone(nowe)
    // Krok już zatwierdzony: zmiana od razu w odpowiedziach (bez czekania na „Dalej”, którego wtedy nie ma).
    // Szkic zawsze nadąża za kaflami: po „Wstecz” odpowiedź znika, a kafle zostają zaznaczone, więc nieaktualny szkic
    // zgubiłby przy następnej odpowiedzi cel dodany po pierwszym „Dalej” (błąd z produkcji 2026-10-26).
    onPodglad?.(nowe)
    if (Array.isArray(wartosc)) onOdpowiedz(nowe)
  }

  useEffect(() => {
    setRobocza((zJednostka ? wartosc?.ile : wartosc) ?? '')
  }, [wartosc, zJednostka])

  // Przy pytaniu z przełącznikiem jednostek odpowiedź jest PARĄ liczba plus
  // jednostka: bez tego „6" znaczyłoby raz sztuki, raz kilowaty.
  const jednostkaAktywna = zJednostka
    ? (pytanie.jednostki.find((j) => j.v === wartosc?.jednostka) ?? pytanie.jednostki[0])
    : {}
  const zbuduj = (x) => (zJednostka
    ? { ile: Number(x), jednostka: jednostkaAktywna.v }
    : Number(x))

  // `komponent`: własna treść pytania (np. karty sprzętu klienta), `pytanie.render({ wartosc, onZmiana })`. Przed
  // „Dalej” zmiany zostają w kroku; w zatwierdzonym kroku idą od razu do odpowiedzi, jak przy wielokrotnym wyborze.
  const komponent = pytanie.typ === 'komponent'
  const [lokalna, setLokalna] = useState(wartosc ?? pytanie.pusta ?? null)
  useEffect(() => { if (komponent && wartosc != null) setLokalna(wartosc) }, [komponent, wartosc])
  const zmienLokalna = (v) => {
    setLokalna(v)
    if (wartosc != null) onOdpowiedz(v)
  }

  return (
    <section
      ref={refEtapu}
      className={`rounded-2xl border bg-white transition-shadow scroll-mt-6 ${
        aktywny ? 'border-blue-300 shadow-md' : 'border-gray-200 shadow-sm'
      }`}
    >
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-start gap-3">
          <span className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold flex-shrink-0 ${
            odpowiedziano ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
          }`}>
            {odpowiedziano ? <CheckIcon className="w-4 h-4" /> : numer}
          </span>
          <div className="min-w-0">
            {/* Ikona pytania ta sama co na osi po prawej (uwaga I2, 2026-10-26): każdy kreator, także falownika. */}
            <h2 className="flex items-center gap-2 text-base font-bold text-gray-900">
              {pytanie.ikona && <FontAwesomeIcon icon={pytanie.ikona} className="h-4 w-4 text-blue-600" aria-hidden="true" />}
              {pytanie.tytul}
            </h2>
            {pytanie.opis && <p className="mt-1 text-xs text-gray-500 leading-5">{pytanie.opis}</p>}
            {pytanie.dopisek?.(odpowiedzi) && <p className="mt-1 text-xs font-medium text-blue-700 leading-5">{pytanie.dopisek(odpowiedzi)}</p>}
          </div>
        </div>
      </div>

      <div className="px-5 pb-5">
        {pytanie.typ === 'liczba' && (
          <div className="flex flex-wrap items-end gap-2">
            {/* ═══ JEDNA LICZBA, DWIE JEDNOSTKI ═══
                „Ile modułów dołożysz" i „ile kilowatów dołożysz" to to samo
                pytanie zadane dwoma językami. Instalator myśli sztukami,
                właściciel domu kilowatami, a rozbicie tego na dwa kroki kazałoby
                jednemu z nich przewijać pytanie, które go nie dotyczy. */}
            {pytanie.jednostki && (
              <div className="inline-flex rounded-lg border-2 border-blue-200 overflow-hidden mr-1">
                {pytanie.jednostki.map((j) => {
                  const wybrana = (wartosc?.jednostka ?? pytanie.jednostki[0].v) === j.v
                  return (
                    <button
                      key={j.v} type="button" aria-pressed={wybrana}
                      onClick={() => onOdpowiedz(robocza === ''
                        ? { jednostka: j.v }
                        : { ile: Number(robocza), jednostka: j.v })}
                      className={`min-h-[44px] px-3 py-2.5 text-xs font-semibold transition-colors ${
                        wybrana ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {j.l}
                    </button>
                  )
                })}
              </div>
            )}
            <div className="relative w-36">
              <input
                type="number" inputMode="decimal"
                min={pytanie.liczba.min} step={pytanie.liczba.krok || 1}
                placeholder={jednostkaAktywna.placeholder ?? pytanie.liczba.placeholder}
                value={robocza}
                onChange={(e) => {
                  setRobocza(e.target.value)
                  // Skasowana liczba to brak odpowiedzi: krok wraca, a z nim „Nie wiem” (przegląd 2026-10-24, D7).
                  if (e.target.value === '' && odpowiedziano) onOdpowiedz(zJednostka ? { jednostka: jednostkaAktywna.v } : undefined)
                }}
                onBlur={() => { if (robocza !== '') onOdpowiedz(zbuduj(robocza)) }}
                onKeyDown={(e) => { if (e.key === 'Enter' && robocza !== '') onOdpowiedz(zbuduj(robocza)) }}
                aria-label={pytanie.tytul}
                className="w-full text-center tabular-nums border-2 border-blue-200 bg-blue-50/40 rounded-lg
                           pl-3 pr-12 py-2.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
                {jednostkaAktywna.jednostka ?? pytanie.liczba.jednostka}
              </span>
            </div>
          </div>
        )}

        {komponent && pytanie.render({ wartosc: lokalna, onZmiana: zmienLokalna })}

        {pytanie.typ === 'szukanie' && (
          <WyborModelu
            pytanie={pytanie} wartosc={wartosc} onOdpowiedz={onOdpowiedz}
            /* Odpowiedź z wcześniejszego kroku (moc obecnych modułów) zawęża
               listę podpowiedzi — patrz `mocZ` przy pytaniu. */
            mocZOdpowiedzi={pytanie.mocZ ? pytanie.mocZ(odpowiedzi) : null}
          />
        )}

        {pytanie.typ === 'lista' && (
          <select
            value={wartosc ?? ''}
            onChange={(e) => onOdpowiedz(e.target.value)}
            aria-label={pytanie.tytul}
            className="w-full sm:w-80 border-2 border-blue-200 bg-blue-50/40 rounded-lg px-3 py-2.5 text-sm
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <option value="">{t('szkielet.wybierz')}</option>
            {pytanie.opcje.map((o) => (
              <option key={o.v} value={o.v}>{o.l}</option>
            ))}
          </select>
        )}

        {/* `kafle`: wielokrotny wybór jako kafle z ikoną i opisem (części rozbudowy, 2026-10-26). */}
        {wielokrotny && pytanie.kafle && (
          <div className="grid sm:grid-cols-3 gap-2" role="group" aria-label={pytanie.tytul}>
            {pytanie.opcje.map((o) => {
              const tak = zaznaczone.includes(o.v)
              return (
                <button
                  key={o.v} type="button" aria-pressed={tak} onClick={() => przelacz(o.v)}
                  className={`relative flex items-start gap-3 text-left rounded-xl border-2 px-4 py-3 transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    tak ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  {o.ikona && (
                    <span className={`flex items-center justify-center h-9 w-9 shrink-0 rounded-lg ${tak ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500'}`}>
                      <FontAwesomeIcon icon={o.ikona} aria-hidden="true" />
                    </span>
                  )}
                  <span className="min-w-0">
                    <span className="block text-sm font-semibold text-gray-900">{o.l}</span>
                    {o.o && <span className="block text-xs text-gray-500 mt-0.5 leading-4">{o.o}</span>}
                  </span>
                  {tak && <CheckIcon className="absolute right-2 top-2 h-4 w-4 text-blue-600" aria-hidden="true" />}
                </button>
              )
            })}
          </div>
        )}
        {wielokrotny && !pytanie.kafle && (
          <div className="flex flex-wrap gap-2" role="group" aria-label={pytanie.tytul}>
            {pytanie.opcje.map((o) => {
              const tak = zaznaczone.includes(o.v)
              return (
                <button
                  key={o.v} type="button" aria-pressed={tak} onClick={() => przelacz(o.v)}
                  className={`inline-flex items-center gap-1.5 min-h-[44px] rounded-full border-2 px-3.5 py-2 text-sm font-semibold transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    tak ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-700 hover:border-blue-300'
                  }`}
                >
                  {tak && <CheckIcon className="h-4 w-4" aria-hidden="true" />}
                  {o.l}
                </button>
              )
            })}
          </div>
        )}

        {(!pytanie.typ || pytanie.typ === 'wybor') && (
          <div className="grid sm:grid-cols-3 gap-2">
            {pytanie.opcje.map((o) => {
              // Bez odpowiedzi kafel z `domyslna(o)` jest zaznaczony wstępnie (zapisuje go dopiero kliknięcie).
              const zaznaczona = (wartosc === undefined ? pytanie.domyslna?.(odpowiedzi) : wartosc) === o.v
              return (
                <button
                  key={o.l} type="button" aria-pressed={zaznaczona}
                  onClick={() => onOdpowiedz(o.v)}
                  className={`text-left rounded-xl border-2 px-3.5 py-3 transition-colors
                              focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${
                    zaznaczona
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                  }`}
                >
                  <span className="block text-sm font-semibold text-gray-900">{o.l}</span>
                  {o.o && <span className="block text-xs text-gray-500 mt-0.5 leading-4">{o.o}</span>}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* ═══ JEDNA STOPKA DLA WSZYSTKICH TYPÓW PYTAŃ ═══
          Wcześniej pole liczbowe miało własny „Zatwierdź", pole z lupą nie miało
          nic, a pominięcie raz było małym szarym napisem, raz pełnowymiarowym
          kafelkiem w rzędzie odpowiedzi. Ten sam ruch wyglądał w każdym kroku
          inaczej. Teraz każdy aktywny etap kończy się tak samo, a „Dalej ↓" jest
          tym samym przyciskiem co w asystencie zestawów. */}
      {aktywny && (
        <div className="flex justify-end items-center gap-2 px-5 pb-4 -mt-1">
          {onWstecz && (
            <button
              type="button" onClick={onWstecz}
              className="mr-auto inline-flex items-center text-xs font-semibold text-gray-600 hover:text-gray-900
                         min-h-[44px] rounded-lg px-2 py-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
              {t('szkielet.wstecz')}
            </button>
          )}
          {pytanie.opcjonalne && (
            /* Wypełnione tło, nie tylko obwódka: przy samej obwódce wyjście
               ginęło obok „Dalej" i człowiek, który po prostu nie zna liczby,
               wpisywał cokolwiek. Nadal jest szare, więc widać, że to nie jest
               droga domyślna.

               `tekstPominiecia` pozwala nazwać SKUTEK, a nie czynność:
               przy budżecie „Nie wiem, pokaż różne ceny" mówi więcej niż
               „pomiń", bo odpowiada na pytanie „a co wtedy dostanę". */
            <button
              type="button" onClick={() => onOdpowiedz(null)}
              className="inline-flex items-center text-xs font-semibold text-gray-700 hover:text-gray-900
                         bg-gray-100 hover:bg-gray-200 border border-gray-300 hover:border-gray-400
                         min-h-[44px] rounded-lg px-3.5 py-2 transition-colors
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
            >
              {pytanie.tekstPominiecia || t('szkielet.pomin')}
            </button>
          )}
          <button
            type="button"
            onClick={() => onOdpowiedz(komponent ? lokalna : wielokrotny ? zaznaczone : (robocza === '' ? null : zbuduj(robocza)))}
            disabled={!pytanie.opcjonalne && pytanie.typ === 'liczba' && robocza === ''}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600
                       hover:text-white hover:bg-blue-600 border border-blue-200 hover:border-blue-600
                       disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-blue-600
                       disabled:hover:border-blue-200 min-h-[44px] rounded-lg px-3.5 py-2 transition-colors
                       focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            {t('szkielet.dalej')}
          </button>
        </div>
      )}
    </section>
  )
}

const KreatorSzkielet = ({
  tytul, opis, pytania, onDobierz, domyslne = {},
  konfiguracjaWynikow, tekstPrzycisku,
  nadPytaniami = null,
  // `bezNaglowka`: kreator osadzony w innej stronie (droga „Kilka pytań” w formularzu zlecenia, 2026-10-26): bez osi po
  // prawej, przycisku powrotu i nagłówka. `wymagane`: klucze pytań, bez których przycisk wyniku jest nieaktywny.
  bezNaglowka = false, wymagane = null,
  // `poczatkowe`: odpowiedzi do wznowienia (np. po cofnięciu w przeglądarce); „Zacznij od nowa” wraca do `domyslne`.
  // `onZmiana(odpowiedzi)`: po każdej zmianie, żeby strona mogła je zapamiętać.
  poczatkowe = null, onZmiana = null,
}) => {
  const { t } = useTranslation('asystent')
  const [odpowiedzi, setOdpowiedzi] = useState(poczatkowe || domyslne)
  useEffect(() => { onZmiana?.(odpowiedzi) }, [odpowiedzi]) // eslint-disable-line react-hooks/exhaustive-deps
  const [wynik, setWynik] = useState(null)
  const [ladowanie, setLadowanie] = useState(false)
  const [blad, setBlad] = useState(null)
  // Zaznaczenia w pytaniach wielokrotnych przed „Dalej”: widoczne od razu na osi po prawej.
  const [robocze, setRobocze] = useState({})
  const wynikiRef = useRef(null)
  const etapyRef = useRef({})

  // Ikona w nagłówku ta sama co przy tym kreatorze w menu „Narzędzia” (config/nawigacja):
  // błyskawica dla falownika, bateria dla magazynu, dom z panelami dla zestawu. Wcześniej
  // wszystkie kreatory miały te same gwiazdki, które właściciel odrzucił 2026-10-08.
  const { pathname } = useLocation()
  const IkonaNaglowka = GRUPY_NARZEDZI.flatMap((g) => g.pozycje)
    .find((p) => czyAktywna(p, pathname))?.Icon || IkonaAsystenta

  useEffect(() => { if (!bezNaglowka) window.scrollTo(0, 0) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ STABILNE WARUNKI WIDOCZNOŚCI (błąd z produkcji 2026-10-26, por. B3) ═══
  // Warunki liczymy z odpowiedzi RAZEM z zaznaczeniami przed „Dalej” (cele), żeby zaznaczenie od razu pokazywało
  // i chowało zależne pytania, a nie dopiero przy następnej odpowiedzi. Odpowiedzi ukrytego pytania nie kasujemy:
  // gdy pytanie wraca, wraca z odpowiedzią; do doboru idą tylko odpowiedzi widocznych pytań (`doWyslania`).
  const zRoboczymi = (o) => {
    const wynik = { ...o }
    pytania.forEach((p) => {
      if (p.typ === 'wielokrotny' && wynik[p.klucz] === undefined && robocze[p.klucz]?.length) wynik[p.klucz] = robocze[p.klucz]
    })
    return wynik
  }
  const efektywne = zRoboczymi(odpowiedzi)
  const widoczne = pytania.filter((p) => !p.warunek || p.warunek(efektywne))
  const doWyslania = Object.fromEntries(Object.entries(efektywne)
    .filter(([k]) => { const p = pytania.find((q) => q.klucz === k); return !p || !p.warunek || p.warunek(efektywne) }))
  // ═══ CO ZNACZY „ODPOWIEDZIANO" ═══
  // Przy pytaniu z przełącznikiem jednostek samo kliknięcie „Rachunek w zł"
  // zapisuje jednostkę BEZ liczby — trzeba ją pamiętać, zanim ktoś zacznie
  // pisać. Bez tego wyjątku taki klik zaliczał pytanie jako odpowiedziane:
  // etap się domykał, przycisk „Dalej" znikał, a w pasku postępu stawało
  // „pominięte", choć człowiek dopiero wybrał, w czym poda wartość.
  const wypelnione = (p) => {
    const v = odpowiedzi[p.klucz]
    if (v === undefined || v === '') return false
    if (p.jednostki) return v === null || v.ile != null
    return true
  }
  const odpowiedziano = widoczne.filter(wypelnione).length
  // Bieżące pytanie to PIERWSZE bez odpowiedzi, a nie to o numerze równym liczbie odpowiedzi: odpowiedzi z adresu
  // (`domyslne`, np. magazyn i budżet ze strony głównej, 2026-10-23) bywają dalej w kolejce i wtedy żadne pytanie
  // nie dostawało przycisku „Dalej”.
  const biezace = widoczne.findIndex((p) => !wypelnione(p))
  const komplet = odpowiedziano === widoczne.length
  // Z listą wymaganych pytań wynik dopiero po nich; bez niej po dowolnej odpowiedzi.
  const cokolwiek = wymagane
    ? wymagane.every((k) => { const p = pytania.find((q) => q.klucz === k); return !p || !widoczne.includes(p) || wypelnione(p) })
    : odpowiedziano > 0

  const przewin = (el) => el?.scrollIntoView({ behavior: 'smooth', block: 'center' })

  const odpowiedz = (klucz, wartosc) => {
    // Zaznaczone cele bez „Dalej” liczą się jako odpowiedź, gdy ktoś przechodzi do kolejnego pytania (D5).
    const nowe = { ...zRoboczymi(odpowiedzi), [klucz]: wartosc }
    setOdpowiedzi(nowe)
    setWynik(null)

    const kolejne = pytania.filter((p) => !p.warunek || p.warunek(nowe))
    const i = kolejne.findIndex((p) => p.klucz === klucz)
    const nastepne = kolejne[i + 1]
    if (nastepne) setTimeout(() => przewin(etapyRef.current[nastepne.klucz]), 120)
  }

  // „Wstecz”: poprzednie pytanie znów bez odpowiedzi (pozostałe zostają), widok wraca do niego.
  const wstecz = (i) => {
    const poprzednie = widoczne[i - 1]
    if (!poprzednie) return
    if (poprzednie.typ === 'wielokrotny' && Array.isArray(odpowiedzi[poprzednie.klucz])) {
      setRobocze((r) => ({ ...r, [poprzednie.klucz]: odpowiedzi[poprzednie.klucz] }))
    }
    setOdpowiedzi((o) => { const nowe = { ...o }; delete nowe[poprzednie.klucz]; return nowe })
    setWynik(null)
    setTimeout(() => przewin(etapyRef.current[poprzednie.klucz]), 60)
  }

  const dobierz = async () => {
    setLadowanie(true); setBlad(null)
    const res = await onDobierz(doWyslania)
    setLadowanie(false)
    if (res.ok) {
      setWynik(res.data)
      setTimeout(() => wynikiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 120)
    } else {
      setBlad(res.error)
    }
  }

  const odNowa = () => {
    setOdpowiedzi(domyslne); setWynik(null); setBlad(null); setRobocze({})
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const opisyKryteriow = useMemo(() => widoczne
    .filter(wypelnione)
    .map((p) => ({ etykieta: p.etykieta, wartosc: opisOdpowiedzi(p, odpowiedzi[p.klucz]) })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [widoczne, odpowiedzi])

  /** Pasek postępu przy prawej krawędzi, ten sam komponent co w asystencie zestawów. */
  const kroki = useMemo(() => {
    const lista = widoczne.map((p, i) => {
      const v = odpowiedzi[p.klucz]
      const opcja = (p.opcje || []).find((o) => o.v === v)
      return {
        id: p.klucz, label: p.etykieta, icon: p.ikona,
        // Pominięte pytanie (odpowiedź null) to „pominięte”, a nie „null” albo „null zł”.
        detail: wypelnione(p)
          ? (v === null && !p.jednostki ? t('szkielet.pominiete')
            : p.typ === 'komponent' ? (p.opisOdpowiedzi?.(v) || t('szkielet.pominiete'))
            : p.jednostki ? opisJednostki(p, v)
            : p.typ === 'liczba' ? `${v} ${p.liczba.jednostka}`
              : p.typ === 'szukanie' ? opisModelu(v)
                : p.typ === 'wielokrotny' ? opisWielu(p, v, { skrot: true })
                : (opcja ? opcja.l : String(v)))
          : (p.typ === 'wielokrotny' && robocze[p.klucz]?.length ? opisWielu(p, robocze[p.klucz], { skrot: true }) : null),
        status: wypelnione(p) ? 'done' : (i === biezace ? 'current' : 'todo'),
        onClick: () => przewin(etapyRef.current[p.klucz]),
      }
    })
    lista.push({
      id: 'wyniki', label: 'Propozycje', icon: faLayerGroup,
      detail: wynik ? `${wynik.propozycje.length} z ${wynik.kandydatow}` : null,
      status: wynik ? 'done' : (komplet ? 'current' : 'todo'),
      onClick: () => wynikiRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    })
    return lista
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widoczne, odpowiedzi, biezace, wynik, komplet, robocze])

  return (
    <div className={bezNaglowka ? '' : 'px-4 md:px-8 lg:px-12 py-8 max-w-6xl mx-auto'}>
      {!bezNaglowka && (
        <>
          <ProgressRail steps={kroki} />

          <div className="mb-5"><BackButton /></div>

          <div className="flex items-start gap-3 mb-6">
            <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-blue-50 flex-shrink-0">
              <IkonaNaglowka className="w-6 h-6 text-blue-600" />
            </span>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{tytul}</h1>
              <p className="mt-1 text-sm text-gray-500 leading-6">{opis}</p>
            </div>
          </div>
        </>
      )}

      {/* Miejsce na przełącznik drogi doboru — dziś używa go tylko asystent
          całego zestawu, bo tylko on ma drugie wejście do tego samego silnika. */}
      {nadPytaniami}

      <div className="space-y-4">
        {widoczne.map((p, i) => (
          <Etap
            key={p.klucz} numer={i + 1} pytanie={p}
            wartosc={odpowiedzi[p.klucz]} odpowiedzi={odpowiedzi}
            aktywny={i === biezace}
            onOdpowiedz={(v) => odpowiedz(p.klucz, v)}
            onPodglad={(v) => setRobocze((r) => ({ ...r, [p.klucz]: v }))}
            onWstecz={i > 0 ? () => wstecz(i) : null}
            refEtapu={(el) => { etapyRef.current[p.klucz] = el }}
          />
        ))}
      </div>

      <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Wynik wolno zobaczyć także po części pytań. Wygląd przycisku niesie
            wtedy informację, że dobór jest mniej precyzyjny: przygaszony błękit
            zamiast pełnego. Blokowanie do kompletu wyrzucałoby z kreatora
            każdego, kto nie zna jednej odpowiedzi. */}
        <button
          type="button" onClick={dobierz} disabled={!cokolwiek || ladowanie}
          className={`px-6 py-3 rounded-xl text-sm font-semibold transition-colors ${
            !cokolwiek || ladowanie
              ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
              : komplet
                ? 'bg-blue-600 hover:bg-blue-500 text-white'
                : 'bg-blue-300 hover:bg-blue-400 text-blue-900'
          }`}
        >
          {ladowanie ? t('szkielet.ladowanie') : (tekstPrzycisku || t('szkielet.pokaz_produkty'))}
        </button>

        {!komplet && (
          <p className="text-xs text-gray-500 leading-5">
            {cokolwiek
              ? t('szkielet.odpowiedziano', { odpowiedziano, wszystkie: widoczne.length })
              : t('szkielet.odpowiedz_pierwsze')}
          </p>
        )}

        {cokolwiek && (
          <button type="button" onClick={odNowa}
                  className="inline-flex min-h-[44px] items-center gap-1.5 px-1 text-xs font-medium text-gray-500 hover:text-gray-700">
            <ArrowPathIcon className="w-4 h-4" />
            {t('szkielet.od_nowa')}
          </button>
        )}
      </div>

      {blad && <p className="mt-4 text-sm text-red-600">{blad}</p>}

      {wynik && (
        <div ref={wynikiRef} className="mt-10 scroll-mt-6">
          {/* Pusta lista bez komunikatu: strona rysuje wynik sama (prosty dobór), więc kreator nie dokłada pustej ramki. */}
          {wynik.propozycje.length === 0 ? (wynik.komunikat ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-6 py-10 text-center">
              <p className="text-sm text-gray-700">{wynik.komunikat}</p>
              <button type="button" onClick={odNowa}
                      className="mt-3 text-sm font-semibold text-blue-600 hover:text-blue-700">
                {t('szkielet.zmien_odpowiedzi')}
              </button>
            </div>
          ) : null) : (
            <>
              <h2 className="text-xl font-bold text-gray-900">{t('szkielet.trzy_propozycje')}</h2>
              <p className="mt-1 mb-5 text-sm text-gray-500">
                <Trans t={t} i18nKey="szkielet.wybrane_sposrod" values={{ liczba: wynik.kandydatow }}
                       components={{ b: <strong className="text-gray-700" /> }} />
                {!komplet && t('szkielet.zawez')}
              </p>
              <WynikiDoboru
                wynik={wynik}
                opisyKryteriow={opisyKryteriow}
                onZmienKryteria={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                {...konfiguracjaWynikow(wynik)}
              />
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default KreatorSzkielet
